import {getServerSession} from "next-auth/next";
import {authOptions} from "@/app/lib/auth";
import {authenticatedFetch} from "@/app/lib/apiUtils";
import {formatInTimeZone} from "date-fns-tz";
import {UpdateOrderResult} from "@/app/api/types";


export const formatWithOffset = (date: Date): string => {
  // Always format in Berlin timezone for consistency
  return formatInTimeZone(
    date,
    'Europe/Berlin',
    "yyyy-MM-dd'T'HH:mm:ss"
  );
};


/**
 * Updates the scheduling details of an existing order (rescheduling).
 *
 * @param orderId - The ID of the order to reschedule.
 * @param scheduledStart - The new start time for the rescheduled order.
 * @param scheduledEnd - The new end time for the rescheduled order.
 *
 * @returns The updated order details as returned from the API.
 *
 * @throws Throws an error if the rescheduling request fails.
 */
export async function updateOrder(
  orderId: string,
  scheduledStart: Date,
  scheduledEnd: Date
): Promise<UpdateOrderResult> {
  console.log('Rescheduling order with times:');
  console.log('Original start:', scheduledStart);
  console.log('Original end:', scheduledEnd);

  const formattedStart = formatWithOffset(scheduledStart);
  const formattedEnd = formatWithOffset(scheduledEnd);

  console.log('Formatted times:');
  console.log('Start:', formattedStart);
  console.log('End:', formattedEnd);

  const response = await fetch(`/api/order/${orderId}/reschedule`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      scheduledStart: formattedStart,
      scheduledEnd: formattedEnd,
    }),
  });

  if (!response.ok) {
    console.error('Failed to reschedule order in OrderUtils');
    const errorText = await response.text();
    console.error('Error details:', errorText);
  }

  const result: UpdateOrderResult = await response.json();
  console.log('API Response in updateOrder:', JSON.stringify(result, null, 2));
  return result;
}


/**
 * Fetches both the order details and guide availability for a given order.
 * REschedule Flow:
 *    a. User selects an existing Booking (orderId)
 *    b. User selects a date from the CustomDatePicker.
 *    c. User selects a time slot from the available options for that date.
 *    d. User reviews booking summary and confirms the booking. (no payment because that already happened)
 * e. User sees either the success/cancel page and we need to update the order accordingly.
 * @param orderId - The ID of the order for which to fetch the data.
 *
 * @returns A combined response containing both the order details and the guide's availability.
 *
 * @throws Throws an error if the fetch request fails.
 */
export async function fetchOrderAndAvailability(orderId: string) {
  const response = await fetch(`/api/order/${orderId}/availability`, {
    method: 'GET',
  });

  if (!response.ok) {
    console.log(response)
    throw new Error('Failed to fetch order and availability');
  }

  return await response.json();
}


/**
 * Fetches both guide details and availability for a NEW booking flow.
 *
 *  Booking Flow:
 *    a. User selects a communication type (call or chat).
 *    b. User chooses a duration for the session.
 *    c. User selects a date from the CustomDatePicker.
 *    d. User selects a time slot from the available options for that date.
 *    e. User reviews booking summary and confirms the booking.
 *    f. User sees either the success/cancel page and we need to update the order accordingly.
 * @param guideId - The ID of the guide for whom to fetch availability.
 *
 * @returns A combined response containing the guide's details and availability data.
 *
 * @throws Throws an error if the fetch request fails.
 */
export async function fetchGuideAndAvailability(guideId: string) {
  const response = await fetch(`/api/guide/${guideId}/availability`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch guide and availability');
  }

  return await response.json();
}


export async function createOrder(
  guideId: string,
  productId: string,
  selectedDate: Date,
  selectedTimeSlot: string,
  duration: number,               // Pass duration directly
  preparationMessage: string,     // Pass preparationMessage as a parameter
  stripePromise: any              // Pass stripePromise as a parameter
) {
  if (!selectedDate || !selectedTimeSlot) {
    console.error("Please select both a date and a time slot.");
    return;
  }

  const dateObject = new Date(selectedDate);
  const [hours, minutes] = selectedTimeSlot.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) {
    console.error("Invalid time format. Please try selecting the time slot again.");
    return;
  }
  dateObject.setHours(hours, minutes, 0, 0);

  const formattedStartDateTime = formatWithOffset(dateObject);
  const endDateTime = addMinutes(dateObject, duration);
  const formattedEndDateTime = formatWithOffset(endDateTime);

  const orderData = {
    customerId: "",  // Add logic for fetching the customerId here
    guideId: guideId,
    productId: productId,
    scheduledStart: formattedStartDateTime,
    scheduledEnd: formattedEndDateTime,
    customerMessage: preparationMessage || "",
    provisionId: ""  // This will be set by the backend
  };

  try {
    const response = await fetch('/api/order', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderData),
    });

    if (!response.ok) {
      throw new Error('Failed to create order');
    }

    const result = await response.json();
    const {sessionId} = result;

    // Handle Stripe Checkout redirect
    if (stripePromise) {
      const stripe = await stripePromise;
      if (stripe) {
        const {error} = await stripe.redirectToCheckout({sessionId});
        if (error) {
          console.error('Stripe redirect error:', error);
        }
      } else {
        console.error('Stripe object is null');
      }
    }

    return result;

  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
}

function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 6000);
}



